import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";
import { getFormatLocale } from "@/i18n/runtime";
import { describeMillis } from "@/lib/duration";
import { LiveDuration } from "@/lib/LiveDuration";

import { StatusBadges } from "./badges";
import { MailToggle } from "./MailToggle";
import type { ServiceTile } from "./serviceOverview";
import { Sparkline } from "./Sparkline";

/**
 * The same services, one line each, for a reader who owns forty of them. No grouping and no
 * section headings by design — the sort does the work, and grouping a table means either
 * repeated headers or a second scroll context.
 *
 * A real table, not a grid: auto layout sizes every column to its widest value across all rows,
 * so counts and badges are never clipped and never wrapped. The one flexible column is LATEST
 * EPISODE — free log text is the only content allowed to truncate.
 *
 * Rows are not click targets: the hand-off to Logs, Traces and Episodes rides the same button
 * trio the cards carry.
 */
export function ServiceTable(props: {
  tiles: ServiceTile[];
  showVolume: boolean;
  /** The described window ("Last 24 h") — the catalog phrases it into the no-episode cell. */
  windowDescribed: string;
  isSubscribed: (tile: ServiceTile) => boolean;
  isViaApplication: (tile: ServiceTile) => boolean;
  onToggleMail: (tile: ServiceTile) => void;
  onLogs: (tile: ServiceTile) => void;
  onTraces: (tile: ServiceTile) => void;
  onEpisodes: (tile: ServiceTile) => void;
}) {
  const t = useT();
  const heading = "px-1.5 text-left font-normal whitespace-nowrap";

  return (
    <div
      data-testid="service-rows"
      className="overflow-x-auto rounded-[11px] border border-[#1E344C] bg-card"
    >
      <table className="w-full border-collapse">
        <thead>
          <tr className="h-8 border-b border-[#17293D] bg-[#0B1826] font-mono text-[10px] tracking-[0.12em] text-[#5F7590]">
            <th className={`${heading} pl-4`}>{t.overview.table.header.service}</th>
            <th className={heading}>{t.overview.table.header.status}</th>
            <th className={`${heading} text-right`}>{t.overview.table.header.errors}</th>
            <th className={`${heading} text-right`}>{t.overview.table.header.warn}</th>
            <th className={`${heading} text-right`}>{t.overview.table.header.logs}</th>
            {props.showVolume && <th className={heading}>{t.overview.table.header.volume}</th>}
            <th className={`${heading} w-full`}>{t.overview.table.header.latestEpisode}</th>
            {/* The button trio's column — the buttons say where they go, a caption would repeat them. */}
            <th className={heading} />
            <th className={`${heading} pr-4`}>{t.overview.table.header.mail}</th>
          </tr>
        </thead>
        <tbody>
          {props.tiles.map(tile => {
            const open = tile.episodes !== undefined && tile.episodes.open > 0;
            const latest = tile.episodes?.latest;
            const logs =
              tile.volume === undefined
                ? undefined
                : tile.volume.error + tile.volume.warn + tile.volume.info + tile.volume.debug;
            return (
              <tr
                key={tile.serviceId}
                data-testid="service-row"
                className={`h-10 border-b border-[#101F31] ${
                  open ? "bg-[rgba(229,84,74,0.07)]" : ""
                }`}
              >
                <td className="py-0 pr-1.5 pl-4 align-middle font-mono text-xs whitespace-nowrap">
                  {tile.label}
                </td>
                <td className="px-1.5 align-middle">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <StatusBadges tile={tile} />
                  </div>
                </td>
                <Cell value={tile.volume?.error} tone="error" />
                <Cell value={tile.volume?.warn} tone="warn" />
                <Cell value={logs} tone="neutral" />
                {props.showVolume && (
                  <td className="px-1.5 align-middle">
                    <div className="w-[90px]">
                      <Sparkline buckets={tile.volume?.buckets} mini />
                    </div>
                  </td>
                )}
                <td className="w-full max-w-0 px-1.5 align-middle">
                  <div className="truncate font-mono text-[11.5px] text-[#DCE8F3]">
                    {tile.episodes === undefined ? (
                      <span className="text-[#4A6480]">—</span>
                    ) : open && latest != null ? (
                      <>
                        <LiveDuration since={latest.openedAt} />
                        {latest.firstMatchDetail != null && ` · ${latest.firstMatchDetail}`}
                      </>
                    ) : latest?.closedAt != null ? (
                      t.overview.table.allClearAgo(
                        describeMillis(Date.now() - Date.parse(latest.closedAt)),
                      )
                    ) : (
                      <span className="text-[#8CA1B8]">
                        {t.overview.table.noEpisodeIn(props.windowDescribed)}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-1.5 align-middle whitespace-nowrap">
                  <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => props.onLogs(tile)}>
                      {t.overview.actions.toLogs}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => props.onTraces(tile)}>
                      {t.overview.actions.toTraces}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => props.onEpisodes(tile)}>
                      {t.overview.actions.toEpisodes}
                    </Button>
                  </div>
                </td>
                <td className="py-0 pr-4 pl-1.5 text-right align-middle whitespace-nowrap">
                  <MailToggle
                    subscribed={props.isSubscribed(tile)}
                    viaApplication={props.isViaApplication(tile)}
                    applicationName={tile.applicationName}
                    onToggle={() => props.onToggleMail(tile)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Cell(props: { value: number | undefined; tone: "error" | "warn" | "neutral" }) {
  const color =
    props.value === undefined || props.value === 0
      ? "text-[#4A6480]"
      : props.tone === "error"
        ? "text-severity-error"
        : props.tone === "warn"
          ? "text-severity-warn"
          : "text-[#B6C8DA]";
  return (
    <td className={`px-1.5 text-right align-middle font-mono text-[11.5px] whitespace-nowrap ${color}`}>
      {props.value === undefined ? "—" : props.value.toLocaleString(getFormatLocale())}
    </td>
  );
}
